import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

const stagedContactSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().optional(),
  title: z.string().optional(),
  is_primary_contact: z.boolean().default(false),
});

type StagedContactFormData = z.infer<typeof stagedContactSchema>;

interface StagedContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onContactAdded: (contact: {
    name: string;
    email?: string;
    phone?: string;
    title?: string;
    is_primary_contact: boolean;
  }) => void;
}

/**
 * Modal for adding contacts during organization creation (before org exists).
 * Unlike AddContactModal, this doesn't make API calls - it just collects data
 * and passes it back via callback for local staging.
 */
export function StagedContactModal({
  open,
  onOpenChange,
  onContactAdded,
}: StagedContactModalProps) {
  const { control, handleSubmit, formState: { errors }, reset } = useForm<StagedContactFormData>({
    resolver: zodResolver(stagedContactSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      title: '',
      is_primary_contact: false,
    },
  });

  const onSubmit = (data: StagedContactFormData) => {
    onContactAdded({
      name: data.name,
      email: data.email || undefined,
      phone: data.phone || undefined,
      title: data.title || undefined,
      is_primary_contact: data.is_primary_contact,
    });
    reset();
    onOpenChange(false);
  };

  const handleCancel = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Contact</DialogTitle>
          <DialogDescription>
            Add a contact for this client. The contact will be created when you save the client.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Name */}
          <div>
            <Label htmlFor="name">Name *</Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  id="name"
                  placeholder="Enter contact name"
                  {...field}
                />
              )}
            />
            {errors.name && (
              <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email">Email <span className="text-gray-500 text-sm">(Optional)</span></Label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input
                  id="email"
                  type="email"
                  placeholder="contact@example.com"
                  {...field}
                />
              )}
            />
            {errors.email && (
              <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="phone">Phone <span className="text-gray-500 text-sm">(Optional)</span></Label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <Input
                  id="phone"
                  type="tel"
                  placeholder="555-1234"
                  {...field}
                />
              )}
            />
          </div>

          {/* Title */}
          <div>
            <Label htmlFor="title">Title <span className="text-gray-500 text-sm">(Optional)</span></Label>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <Input
                  id="title"
                  placeholder="e.g., Project Manager"
                  {...field}
                />
              )}
            />
          </div>

          {/* Primary Contact */}
          <div className="flex items-center space-x-2">
            <Controller
              name="is_primary_contact"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="is_primary_contact"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label
              htmlFor="is_primary_contact"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Set as primary contact
            </Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit">Add Contact</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
